import React, { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';

interface AudioPlayerProps {
  systemId: string;
  archivePath: string;
  filename: string;
}

function AudioPlayer({ systemId, archivePath, filename }: AudioPlayerProps) {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);
  // The cached pre-signed URL expires after 15 min — on the first <audio>
  // error we refetch it once and retry. Second failure = visible error state.
  const retriedRef = useRef(false);

  const getS3Key = (): string | null => {
    // Convert archive path to S3 key
    // archivePath: /volume1/archive/3cx-legacy/archive/2025/11/2025-11-19/file.wav
    // S3 key: 3cx-legacy/2025/11/2025-11-19/file.wav
    if (archivePath) {
      const match = archivePath.match(/\/volume1\/archive\/([^/]+)\/archive\/(.+)/);
      if (match) {
        return `${match[1]}/${match[2]}`;
      }
    }
    // No fallback: `${systemId}/${filename}` never matches the real key
    // layout, so an unparseable path is an error — not a guess.
    return null;
  };

  const fetchPlaybackUrl = async (): Promise<string> => {
    const s3Key = getS3Key();
    if (!s3Key) {
      throw new Error(`Unrecognized archive path: ${archivePath || '(empty)'}`);
    }
    const data = await api.getPlaybackUrl(s3Key);
    return data.url;
  };

  // Auto-play once the <audio> element renders with a URL — awaiting the
  // play() promise instead of a fixed timeout.
  useEffect(() => {
    if (!audioUrl) return;
    const el = audioRef.current;
    if (!el) return;
    el.play()
      .then(() => setPlaying(true))
      .catch(() => { /* load failures hit onError; autoplay blocks leave the controls usable */ });
  }, [audioUrl]);

  const handlePlay = async () => {
    if (audioUrl) {
      // Already have URL, just toggle play/pause
      const el = audioRef.current;
      if (!el) return;
      if (playing) {
        el.pause();
        setPlaying(false);
      } else {
        try {
          await el.play();
          setPlaying(true);
        } catch { /* load failures hit onError on the element */ }
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = await fetchPlaybackUrl();
      setAudioUrl(url); // effect above starts playback
    } catch (err: any) {
      setError('Audio not available');
      console.error('Playback error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAudioError = async () => {
    setPlaying(false);
    if (!retriedRef.current) {
      // First failure — the pre-signed URL likely expired. Refetch once and retry.
      retriedRef.current = true;
      try {
        const url = await fetchPlaybackUrl();
        const el = audioRef.current;
        if (url === audioUrl && el) {
          // Same URL string — React won't touch src, force a reload.
          el.load();
          el.play().then(() => setPlaying(true)).catch(() => { /* onError fires again */ });
        } else {
          setAudioUrl(url); // effect above retries playback
        }
        return;
      } catch { /* fall through to the error state */ }
    }
    setError('Playback failed');
    console.error('Playback error: audio element failed for', archivePath || filename);
  };

  const handleEnded = () => {
    setPlaying(false);
  };

  if (error) {
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 4,
        background: '#ffebee',
        color: '#c62828',
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}>
        {error}
      </span>
    );
  }

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button
        onClick={handlePlay}
        disabled={loading}
        aria-label={playing ? 'Pause recording' : 'Play recording'}
        aria-pressed={playing}
        style={{
          background: playing ? '#e74c3c' : '#27ae60',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          padding: '4px 10px',
          cursor: loading ? 'wait' : 'pointer',
          fontSize: 12,
          fontWeight: 600,
          minWidth: 50,
        }}
        title={playing ? 'Pause' : 'Play'}
      >
        {loading ? '...' : playing ? '⏸ Pause' : '▶ Play'}
      </button>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleEnded}
          onError={handleAudioError}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          controls
          style={{ height: 30, maxWidth: 250 }}
        />
      )}
    </div>
  );
}

export default AudioPlayer;
